import { v4 as uuidv4 } from 'uuid';
import ComponentLink from '../application-core/ComponentLink';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import { Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import WarningIcon from '@material-ui/icons/Warning';
import InputAdornment from '@material-ui/core/InputAdornment';
import { BallBeat } from 'react-pure-loaders';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { hotkeys } from 'react-keyboard-shortcuts';
import DialogMultiActions from '../core/DialogMultiActions';
import Errored from '../core/Errored';
import ListResult from '../core/ListResult';
import PrimaryAddButton from '../core/PrimaryAddButton';
import PrimaryRepeatButton from '../core/PrimaryRepeatButton';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledTextField from '../core/TitledTextField';
import { getGraphFromProject, makeCancelable, sanitiseText, sortByLength, computeConceptIcon, isRTL } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import IRIentry from '../core/IRIentry';
import { MAX_LABEL_LENGTH } from '../core/utils/constants';

class CreateDataResourceDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            acOpen: false,
            checking: false,
            checked: false,
            processingIRI: false,
            isDuplicate: false,
            label: "",
            addedResources: [],
            resourceIRI: "",
            computedIRI: "",
            uniqueIRI: props.useCounterIRI ? true : false,
            viewIRI: false,
            iriRejected: false,
            searchID: null,
            isErrored: null
        };
        this.cancellable = null;
        this.textInput = React.createRef();
    }

    hot_keys = {
        'alt+n': {
            priority: 1,
            handler: () => this.handleClickOpen()
        }
    };

    handleClickOpen = () => {
        this.setState({ open: true, acOpen: false });
        if (this.props.onOpen) this.props.onOpen();
    };

    findResource = (value, lang) => {
        let searchParts = [{
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: this.props.searchClassFilter
        },
        {
            predicate: "http://www.w3.org/2000/01/rdf-schema#label",
            value: value,
            isExactMatch: false
        }];
        this.doAutocomplete(
            {
                search: searchParts,
                language: lang,
                exclude: this.props.exclude,
                includeDeleted: true,
                graph: [getGraphFromProject(this.props.projectIRI)],
                predicates: [
                    "http://www.w3.org/2000/01/rdf-schema#label",
                    "https://grafsync.graphifi.com/schema/gs/inDataset",
                    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    "http://www.w3.org/2002/07/owl#deprecated",
                    "https://grafsync.graphifi.com/schema/gs/status",
                    "https://grafsync.graphifi.com/schema/gs/pendingDeletion"
                ]
            })
            .promise
            .then((data) => {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                // Ignore 'old' searches
                if (data.uuid && data.uuid !== this.state.searchID) return;
                const sortedResults = data.results && data.results.length > 0 ? sortByLength([...data.results], 'label', lang) : null;
                this.setState({
                    results: sortedResults,
                    acOpen: data.results && data.results.length > 0 ? true : false
                });
                const isDuplicate = data.results && data.results.length > 0 ? data.results.some(result => {
                    // We may get an edge case of mutiple labels in a single language
                    if (Array.isArray(result.label[lang])) {
                        return result.label[lang].some(label => label.toLowerCase() === this.state.label.toLowerCase());
                    }
                    else return result.label[lang].toLowerCase() === this.state.label.toLowerCase();
                }) : false;
                this.setState({ isDuplicate: isDuplicate, checked: true, checking: false });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    // We keep a record of added labels to add to duplicate test because quickly added may not have reached DB
    updateAddedResources = () => {
        this.setState((prevState) => {
            const ARs = [...prevState.addedResources];
            ARs.push(prevState.label);
            return { addedResources: ARs };
        });
    };

    addExistingNode = (iri) => {
        this.setState({ open: false, label: "", acOpen: false });
        this.props.onAddExistingNode(iri);
        this.props.onClose && this.props.onClose();
    };

    reserveIDs = async (resourceIRI) => {
        this.setState({ processingIRI: true });
        // Make IRI reservation call
        const isFound = await GS.reserveIRI(resourceIRI, this.props.graph, this.props.searchClass);
        const exists = isFound.exists;
        if (exists) {
            this.rejectIRI(true);
            return false;
        }
        const reservationID = isFound.id;
        return {
            reservationID
        };
    };

    getResourceCounter() {
        const { componentIRI, graph, counters } = this.props;
        this.cancellable = makeCancelable(GS.getCounter(componentIRI, graph, "primary", counters.primaryCounter));
        return this.cancellable;
    }

    getIDsByCounter = async () => {
        let ids;
        let resourceIRI;
        let tries = 0;
        while (!ids || ids === false) {
            tries++;
            if (tries > 20) {
                throw new Error("Cannot get unique IRIs based on counters.");
            }
            const countData = await this.getResourceCounter().promise;
            const count = countData.count;
            resourceIRI = GS.computeCounterIRI(this.props.componentIRI, this.props.useSlashIRI, count);
            ids = await this.loadIDs(resourceIRI).promise;
        }
        return {
            ids,
            resourceIRI
        };
    };

    loadIDs(resourceIRI) {
        this.cancellable = makeCancelable(this.reserveIDs(resourceIRI));
        return this.cancellable;
    }

    addResource = async () => {
        try {
            let resourceIRI = this.state.resourceIRI;
            let ids;
            if (this.props.useCounterIRI) {
                const counterInfo = await this.getIDsByCounter();
                ids = counterInfo.ids;
                resourceIRI = counterInfo.resourceIRI;
            }
            else {
                ids = await this.loadIDs(resourceIRI).promise;
            }            
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ open: false, acOpen: false, resourceIRI }, async () => {
                    try {
                        GS.handle("add-resource");
                        await this.processResource(ids, true);
                    }
                    catch (e) {
                        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                    }
                });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    addResourcePlus = async () => {
        try {
            let resourceIRI = this.state.resourceIRI;
            let ids;
            if (this.props.useCounterIRI) {
                const counterInfo = await this.getIDsByCounter();
                ids = counterInfo.ids;
                resourceIRI = counterInfo.resourceIRI;
            }
            else {
                ids = await this.loadIDs(resourceIRI).promise;
            }
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ resourceIRI }, async () => {
                    try {
                        GS.handle("add-resource-plus");
                        await this.processResource(ids);
                        this.textInput && this.textInput.current && this.textInput.current.focus();
                    }
                    catch (e) {
                        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                    }
                });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    processResource = async (ids, close = false) => {
        // We need to make sure we have the parent loaded so narrower events happens
        const { reservationID } = ids;
        this.updateAddedResources();
        const { label, resourceIRI } = this.state;
        this.setState({
            label: "",
            computedIRI: "",
            resourceIRI: "",
            processingIRI: false,
            uniqueIRI: this.props.useCounterIRI ? true : false,
            iriRejected: false,
            viewIRI: false,
            acOpen: false
        }, async () => {
            if (close) {
                this.props.onClose && this.props.onClose();
            }
            try {
                GS.handle("process-resource");
                await this.props.onAddNewNode(resourceIRI, reservationID, label.trim());
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }
        });
    };

    exitAddResource = () => {
        this.setState({
            open: false,
            label: "",
            computedIRI: "",
            resourceIRI: "",
            acOpen: false,
            processingIRI: false,
            uniqueIRI: this.props.useCounterIRI ? true : false,
            iriRejected: false,
            viewIRI: false
        });
        this.props.onClose && this.props.onClose();
    };

    handleChange = (lang) => (event) => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ label: cleanText, checking: true, checked: false });
        if (cleanText.length > 0) {
            try {
                if (!this.props.useCounterIRI && (!this.props.useUuidIRI || this.state.computedIRI === "")) {
                    const computedIRI = GS.computeIRI(this.props.componentIRI, cleanText, this.props.useSlashIRI, this.props.useUuidIRI, this.props.iriOptions);
                    this.setState({ computedIRI });
                }
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                return;
            }
            this.hcTimeout = setTimeout(() => {
                if (this.state.addedResources.includes(cleanText)) {
                    this.setState({ isDuplicate: true, checking: false, checked: true });
                    this.handleClickAway();
                }
                else this.findResource(cleanText, lang);
            }, 250);
        }
        else {
            const searchID = uuidv4();
            this.handleClickAway();
            this.setState({ searchID, computedIRI: "", checking: false, checked: true, isDuplicate: false });
        }
    };

    resetIRIconfirmation = () => this.setState({ uniqueIRI: false, iriRejected: false });

    confirmUniqueIRI = (iri) => this.setState({ uniqueIRI: true, resourceIRI: iri, iriRejected: false });

    rejectIRI = (processingIRI = false) => this.setState({ uniqueIRI: false, iriRejected: true, processingIRI });

    doAutocomplete(payload) {
        const searchID = uuidv4();
        this.setState({ searchID });
        this.cancellable = makeCancelable(GS.autocomplete(payload, searchID));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    handleClickAway = () => this.setState({ acOpen: false });

    dialogClick = () => this.setState({ acOpen: false });

    render() {

        const { label, acOpen, isErrored, results, isDuplicate, addedResources, checking, processingIRI,
            checked, computedIRI, uniqueIRI, iriRejected } = this.state;
        const { classes, button, defaultLang, title, placeholder, disableMultiple, useCounterIRI, maxLength,
            existing, componentIRI, graph, useWorkflow } = this.props;

        if (isErrored) return <Errored dataTest="resource-error" data={isErrored} />;

        const invalid = label.length === 0 || checking || !checked || !uniqueIRI;
        const rtl = isRTL(defaultLang);

        return (
            <React.Fragment>
                <span onClick={this.handleClickOpen}>{button}</span>
                {this.state.open &&
                    <Dialog
                        onClick={this.dialogClick}
                        data-test="create-resource"
                        maxWidth="md"
                        fullWidth
                        classes={{
                            paper: classes.paper,
                            paperWidthMd: classes.largeDialog
                        }}
                        className={classes.dialog}
                        open={this.state.open}
                        onClose={this.exitAddResource}
                        aria-labelledby="create-resource-title">
                        <DialogTitle
                            data-test="create-resource-title"
                            disableTypography
                            className={classes.mainHeading}
                            id="create-resource-title">{title || 'Create New Resource'}</DialogTitle>
                        <DialogContent style={{ height: '240px' }}>
                            <form autoComplete="off" noValidate="novalidate">
                                <TitledTextField
                                    dataTest="resource-label"
                                    name="resource-label"
                                    title={"Label"}
                                    autoFocus={true}
                                    inputRef={this.textInput}
                                    required={true}
                                    text={label}
                                    adornment={
                                        checking ?
                                            <Tooltip title="Searching for results">
                                                <CircularProgress data-test="searching-for" color="secondary" size={20} />
                                            </Tooltip>
                                            :
                                            iriRejected ?
                                                <InputAdornment position="end">
                                                    <Tooltip title="There is an IRI clash">
                                                        <WarningIcon data-test="warning" />
                                                    </Tooltip>
                                                </InputAdornment>
                                                :
                                                null
                                    }
                                    placeholder={placeholder || "Label"}
                                    maxLength={maxLength}
                                    lang={defaultLang}
                                    helperText={
                                        addedResources.includes(label) ? "You added this value recently." : ""
                                    }
                                    onEnter={!isDuplicate && checked ? this.addResource : null}
                                    onCtrlEnter={!isDuplicate && checked ? this.addResourcePlus : null}
                                    onChange={this.handleChange(defaultLang)} />
                                {acOpen &&
                                    <ClickAwayListener onClickAway={this.handleClickAway}>
                                        <List
                                            data-test="list-results"
                                            dense
                                            className={classes.existingResults}
                                            subheader={<ListSubheader
                                                data-test="list-sub-header"
                                                style={{ ...(rtl) && { textAlign: 'right' } }}
                                                className={classes.existingResultsHeader}>Existing Resources</ListSubheader>}
                                            style={{ marginTop: -8 }}>
                                            {results && results.map((result, i) => {
                                                const component = result.inDataset;
                                                let label = result.label && result.label[defaultLang] ?
                                                    (
                                                        Array.isArray(result.label[defaultLang]) ?
                                                            result.label[defaultLang].sort().join(" AND ")
                                                            :
                                                            result.label[defaultLang]
                                                    )
                                                    :
                                                    result.id;
                                                let additionalLabel = "";
                                                const isIn = result.inDataset && result.inDataset.includes(componentIRI);
                                                if (component && !isIn) {
                                                    additionalLabel = <div>
                                                        {result.inDataset.map(ds => <ComponentLink
                                                            margin={true}
                                                            key={ds}
                                                            id={ds}
                                                            handleNotAvailable={true}
                                                            graph={graph}
                                                            defaultLang={defaultLang} />
                                                        )}
                                                    </div>;
                                                }
                                                const invalid = isIn || result.invalid || (existing && existing.includes(result.id)) || result.deprecated;
                                                let listSecondIcon = computeConceptIcon(result, useWorkflow);
                                                return <ListResult
                                                    key={i}
                                                    text={label}
                                                    iri={result.id}
                                                    lang={defaultLang}
                                                    pendingDeletion={result.pendingDeletion}
                                                    listSecondIcon={listSecondIcon}
                                                    invalid={invalid}
                                                    onClick={!invalid ? () => this.addExistingNode(result.id) : null}
                                                    additionalLabel={additionalLabel} />;
                                            }
                                            )}
                                        </List>
                                    </ClickAwayListener>
                                }
                                {!useCounterIRI &&
                                    <IRIentry
                                        key={computedIRI}
                                        disabled={processingIRI || label === ""}
                                        startIRI={computedIRI}
                                        graph={graph}
                                        componentIRI={componentIRI}
                                        resetIRIconfirmation={this.resetIRIconfirmation}
                                        confirmUniqueIRI={this.confirmUniqueIRI}
                                        changeIRI={() => { }}
                                    />
                                }
                            </form>
                        </DialogContent>
                        {processingIRI ?
                            <DialogActions className={classes.dialogActions}>
                                <div style={{ display: 'flex', height: 40 }}>
                                    <Typography className={classes.subMainHading}>Processing ...</Typography>
                                    <span style={{ marginLeft: 24 }}>
                                        <BallBeat color='#97d700' loading={true} />
                                    </span>
                                </div>
                            </DialogActions>
                            :
                            <DialogMultiActions onCancel={this.exitAddResource}>
                                <PrimaryAddButton title="Add" disabled={invalid} onClick={this.addResource} />
                                {!disableMultiple &&
                                    <PrimaryRepeatButton
                                        dataTest="add-multiple-resources"
                                        title="Add and create another (Ctrl+Enter)"
                                        disabled={invalid}
                                        onClick={this.addResourcePlus} />
                                }
                            </DialogMultiActions>
                        }
                    </Dialog>
                }
            </React.Fragment>
        );
    }
}

CreateDataResourceDialog.defaultProps = {
    maxLength: MAX_LABEL_LENGTH,
    useWorkflow: false
};

CreateDataResourceDialog.propTypes = {
    classes: PropTypes.object,
    onOpen: PropTypes.func,
    source: PropTypes.string,
    onClose: PropTypes.func,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    useCounterIRI: PropTypes.bool,
    counters: PropTypes.object,
    useWorkflow: PropTypes.bool,
    graph: PropTypes.string.isRequired,
    componentIRI: PropTypes.string.isRequired,
    projectIRI: PropTypes.string.isRequired,
    disableMultiple: PropTypes.bool,
    existing: PropTypes.array,
    button: PropTypes.object,
    defaultLang: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    exclude: PropTypes.array,
    title: PropTypes.string,
    maxLength: PropTypes.number,
    onAddExistingNode: PropTypes.func.isRequired,
    onAddNewNode: PropTypes.func.isRequired,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(hotkeys(CreateDataResourceDialog));
